/**
 * IngredientVendorsSection Component
 * Manages vendor associations for a specific ingredient
 * Displays list of associated vendors with preferred toggle
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Star, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { SearchableDropdown } from '../ui/searchable-dropdown';
import { useUIStore } from '@/stores/uiStore';
import { fetchVendors } from '@/lib/api/vendors';
import {
  fetchIngredientVendors,
  addIngredientVendor,
  updateIngredientVendor,
  removeIngredientVendor,
} from '@/lib/api/ingredientVendors';
import type { IngredientVendor, Vendor } from '@/types/api';

interface IngredientVendorsSectionProps {
  ingredientId: number;
  onChange?: (vendors: IngredientVendor[]) => void;
}

export function IngredientVendorsSection({ ingredientId, onChange }: IngredientVendorsSectionProps) {
  const [vendors, setVendors] = useState<IngredientVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  const { showToast } = useUIStore();
  const latestLoadRequestIdRef = useRef(0);
  const vendorsRef = useRef(vendors);
  vendorsRef.current = vendors;

  const emitChange = useCallback((nextVendors: IngredientVendor[]) => {
    onChange?.(nextVendors);
  }, [onChange]);

  const loadVendors = useCallback(async () => {
    const requestId = ++latestLoadRequestIdRef.current;
    setLoading(true);
    try {
      const data = await fetchIngredientVendors(ingredientId);
      if (requestId !== latestLoadRequestIdRef.current) return;
      setVendors(data);
      emitChange(data);
    } catch {
      if (requestId !== latestLoadRequestIdRef.current) return;
      setVendors([]);
      showToast('Failed to load vendors', 'error');
    } finally {
      if (requestId !== latestLoadRequestIdRef.current) return;
      setLoading(false);
    }
  }, [emitChange, ingredientId, showToast]);

  useEffect(() => {
    void loadVendors();
  }, [loadVendors]);

  const handleSearchVendors = useCallback(async (query: string): Promise<Vendor[]> => {
    const results = await fetchVendors({ search: query, isActive: true });
    // Filter out already-associated vendors
    const associatedIds = new Set(vendorsRef.current.map(v => v.vendorId));
    return results.filter(v => !associatedIds.has(v.id));
  }, []);

  async function handleAddVendor(vendor: Vendor) {
    setSaving(true);
    try {
      const created = await addIngredientVendor(ingredientId, {
        vendorId: vendor.id,
      });
      const nextVendors = [...vendorsRef.current, created];
      setVendors(nextVendors);
      emitChange(nextVendors);
      showToast(`Added ${vendor.name}`, 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add vendor';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleTogglePreferred(vendorId: number, current: boolean) {
    const newPreferred = !current;
    const previousVendors = vendorsRef.current;
    const nextVendors = previousVendors.map((iv) => {
      if (iv.vendorId === vendorId) return { ...iv, isPreferred: newPreferred };
      if (newPreferred && iv.isPreferred) return { ...iv, isPreferred: false };
      return iv;
    });

    setSaving(true);
    setVendors(nextVendors);

    try {
      await updateIngredientVendor(ingredientId, vendorId, { isPreferred: newPreferred });
      emitChange(nextVendors);
    } catch {
      setVendors(previousVendors);
      showToast('Failed to update preferred vendor', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveVendor(vendorId: number) {
    const previousVendors = vendorsRef.current;
    const vendorName = previousVendors.find((iv) => iv.vendorId === vendorId)?.vendor?.name || 'Vendor';
    const nextVendors = previousVendors.filter((iv) => iv.vendorId !== vendorId);

    setVendors(nextVendors);
    setConfirmRemove(null);
    setSaving(true);

    try {
      await removeIngredientVendor(ingredientId, vendorId);
      emitChange(nextVendors);
      showToast(`Removed ${vendorName}`, 'success');
    } catch {
      setVendors(previousVendors);
      showToast('Failed to remove vendor', 'error');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading vendors...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-foreground">Vendor Associations</h4>
        {vendors.length > 0 && (
          <span className="text-xs text-muted-foreground">
            {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'}
          </span>
        )}
      </div>

      {/* Add vendor search */}
      <SearchableDropdown<Vendor>
        placeholder="Search vendors to add..."
        onSearch={handleSearchVendors}
        onSelect={handleAddVendor}
        renderItem={(vendor) => (
          <span className="text-sm">{vendor.name}</span>
        )}
        getItemKey={(vendor) => vendor.id}
        minSearchLength={1}
        disabled={saving}
        emptyMessage="No matching vendors found"
        className="w-full"
      />

      {/* Vendor list */}
      {vendors.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          No vendors associated yet. Search above to add one.
        </p>
      ) : (
        <div className="space-y-2">
          {vendors.map((iv) => (
            <div
              key={iv.vendorId}
              className="flex items-center gap-2 p-2 rounded-md border bg-background"
            >
              {/* Preferred star */}
              <button
                onClick={() => handleTogglePreferred(iv.vendorId, iv.isPreferred)}
                className="p-1 rounded hover:bg-accent transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title={iv.isPreferred ? 'Remove preferred status' : 'Set as preferred vendor'}
                aria-label={iv.isPreferred ? 'Remove preferred status' : 'Set as preferred vendor'}
                disabled={saving}
              >
                <Star
                  className={`h-4 w-4 ${
                    iv.isPreferred
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground'
                  }`}
                />
              </button>

              {/* Vendor info */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="text-sm font-medium truncate">
                  {iv.vendor?.name || `Vendor #${iv.vendorId}`}
                </span>
                {iv.isPreferred && (
                  <Badge variant="secondary" className="text-xs">
                    Preferred
                  </Badge>
                )}
              </div>

              {/* Remove button */}
              {confirmRemove === iv.vendorId ? (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-6 px-2 text-xs"
                    onClick={() => handleRemoveVendor(iv.vendorId)}
                  >
                    Remove
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs"
                    onClick={() => setConfirmRemove(null)}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmRemove(iv.vendorId)}
                  className="p-1 rounded hover:bg-destructive/10 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove vendor"
                  aria-label="Remove vendor"
                  disabled={saving}
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
